import React, { useRef, useState } from 'react'
import { Loader, SortableTable, Button, Popup, Checkbox } from 'components/UIElements'
import { browserHistory } from 'utils/navigation'
import { getLatestVersion } from '../../routes/CreateStudy/utils/studyUtils'
import { VERSION_STATUS_MAP } from 'utils/constants'
import { getStudyVersionDestinationPath } from '../utils/studyCardNavigation'

const headerOptions = [
  { key: 'studyStatus', sortable: true, component: 'Study Status' },
  { key: 'studyVersion', sortable: true, component: 'Study Version' },
  { key: 'promotedBy', sortable: true, component: 'Promoted By' },
  { key: 'promoted', sortable: true, component: 'Promoted' },
  { key: 'lastEditedBy', sortable: true, component: 'Last Edited By' },
  { key: 'lastEdited', sortable: true, component: 'Last Edited' },
]

const SelectStudyVersionModal = ({
  studyVersionList,
  studyVersionTableList,
  fetchStudyVersionList,
  createNewStudyVersion,
  studyId,
  redirectPath,
}) => {
  const isLoading = false
  const [unblockCreateNewVersion, setUnblockCreateNewVersion] = useState(false)
  const [isCreatingVersion, setIsCreatingVersion] = useState(false)
  const isCreatingVersionRef = useRef(false)

  const latestVersionIdsByStatus = {
    [VERSION_STATUS_MAP.dev]: getLatestVersion(studyVersionList, VERSION_STATUS_MAP.dev)?.version_id,
    [VERSION_STATUS_MAP.uat]: getLatestVersion(studyVersionList, VERSION_STATUS_MAP.uat)?.version_id,
    [VERSION_STATUS_MAP.live]: getLatestVersion(studyVersionList, VERSION_STATUS_MAP.live)?.version_id,
  }

  const devVersion = studyVersionList?.find(version => version?.version_status.toLowerCase() === VERSION_STATUS_MAP.dev)

  const handleCreateNewVersionClick = async () => {
    if (isCreatingVersionRef.current) return

    isCreatingVersionRef.current = true
    setIsCreatingVersion(true)
    try {
      await createNewStudyVersion(studyId)
      await fetchStudyVersionList(studyId)
    } finally {
      isCreatingVersionRef.current = false
      setIsCreatingVersion(false)
    }
  }

  const createButtonHidden = !!devVersion

  const renderStudyVersion = (rowProps, rowIdx) => {
    const { value, notes = '', idx } = rowProps
    const cellIdx = idx !== undefined ? idx : rowIdx

    return (
      <td key={`study-version-${cellIdx}`} className='study-version-cell'>
        <div>{value}</div>
        {!!notes ? (
          <Popup hover noPointer align='left' position='bottom' dark trigger={<i className='fas fa-info-circle' />}>
            <p>{notes}</p>
          </Popup>
        ) : null}
      </td>
    )
  }

  const renderFunctions = {
    studyVersion: renderStudyVersion,
  }

  const isRowClickable = row => {
    const status = row?.[0]?.value?.toLowerCase()
    const versionId = row?.[1]?.id
    return latestVersionIdsByStatus[status] === versionId
  }

  return (
    <div>
      <div className='title'>Select Study Version</div>
      <div>
        {isLoading ? (
          <Loader inContainer size={5} />
        ) : (
          <div>
            <SortableTable
              id='participant-visits-table'
              className='study-version-list-table'
              headerOptions={headerOptions}
              renderFunctions={renderFunctions}
              rowList={studyVersionTableList}
              emptyText='No Available Study Versions'
              sortingBy='promoted'
              initialOrder='ascending'
              rowCanClick={isRowClickable}
              onRowClick={row => {
                if (isRowClickable(row)) {
                  browserHistory.push(getStudyVersionDestinationPath(studyId, row?.[1]?.id, redirectPath))
                }
              }}
            />
          </div>
        )}
      </div>
      {!createButtonHidden ? (
        <div className='buttons-box'>
          <Button
            disabled={!unblockCreateNewVersion || isCreatingVersion}
            loading={isCreatingVersion}
            id='modal-confirm'
            className='confirm'
            content='Create New Version'
            onClick={handleCreateNewVersionClick}
          />
          <Checkbox
            disabled={isCreatingVersion}
            checked={unblockCreateNewVersion}
            onClick={() => {
              if (isCreatingVersionRef.current) return
              setUnblockCreateNewVersion(!unblockCreateNewVersion)
            }}
            label='Creation of new version cannot be undone'
          />
        </div>
      ) : null}
    </div>
  )
}

export default SelectStudyVersionModal
